/**
 * Helper utility functions for general application logic
 */

/**
 * Returns formatted copyright string with dynamic current year
 * @param {string} appName - Optional application name (default: "DossierGenAI")
 * @returns {string} Copyright text string
 */
export const getCopyrightText = (appName = "DossierGenAI") => {
  const year = new Date().getFullYear();
  return `© ${year} ${appName}. All Rights Reserved.`;
};

/**
 * Returns current full year
 * @returns {number} Full year
 */
export const getCopyrightYear = () => {
  return new Date().getFullYear();
};

/**
 * Converts a string to camel case (actually PascalCase in this implementation)
 * @param {string} str - String to convert
 * @returns {string} Converted string
 */
export const toCamelCase = (str) => {
  if (!str || typeof str !== "string") {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Extract 1-2 letter uppercase initials from a user's name
 * @param {string} name
 * @returns {string} Initials string (defaults to "U")
 */
export const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "U";
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

// ─── Prompt Manager helpers ───────────────────────────────────────────────────

/**
 * Derive a 2-letter country code from a country name.
 * @param {string} name
 * @returns {string}
 */
export const getCountryCode = (name = "") => {
  if (!name) return "NA";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return words[0].slice(0, 2).toUpperCase();
};

/**
 * Format a date/timestamp value into "DD Mon'YY" display form.
 * If the value already looks pre-formatted (contains "'") it is returned as-is.
 * @param {string|number|null} val
 * @returns {string}
 */
export const formatLastModified = (val) => {
  if (!val) return "";
  try {
    if (typeof val === "string" && val.includes("'")) return val;
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const day = String(d.getDate()).padStart(2, "0");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[d.getMonth()];
    const year = String(d.getFullYear()).slice(-2);
    return `${day} ${month}'${year}`;
  } catch {
    return String(val);
  }
};

/**
 * Map a raw /pm/countries API record to the shape expected by CountryCard.
 * @param {object} item  - Raw API record
 * @param {number} index - Array index (used as fallback ID)
 * @returns {object}
 */
export const mapApiCountry = (item, index) => {
  const name = item.country || item.name || `Country ${index + 1}`;
  const id = (item.country || item.id || `country-${index + 1}`)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const code = item.code || getCountryCode(name);
  const prompts = item.property_tags ?? item.prompts ?? 0;
  const uniquePrompts = item.unique_property_tags ?? 0;
  const onboarded = formatLastModified(item.last_modified_at || item.onboarded);
  const status = item.status || "In Progress";

  return {
    id,
    code,
    name,
    prompts,
    uniquePrompts,
    onboarded,
    lastModifiedAt: item.last_modified_at,
    status,
    raw: item,
  };
};

/**
 * Map a raw /pm/properties API record to the shape expected by the MasterTable
 * and PromptAssistant.
 * @param {object} item  - Raw API record
 * @param {number} index - Array index (used as fallback key)
 * @returns {object}
 */
export const mapApiProperty = (item, index) => ({
  id: item.id ?? `prop-${index}`,
  property: item.property ?? "",
  question: item.question ?? "",
  instruction: item.instruction ?? "",
  regOneObject: item.regone_document_object ?? item.regOneObject ?? "",
  source: item.source ?? "",
  technicalFormulation: item.technical_or_formulation ?? item.technicalFormulation ?? "",
  classification: item.classification ?? "",
  studyCategory: item.study_category ?? "",
  studySubCategory: item.study_sub_category ?? "",
  studyTypeRegone: item.study_type_regone ?? "",
  lastModified: formatLastModified(item.timestamp ?? item.lastModified),
  raw: item,
});

